/**
 * useMonitoringTab — singleton lazy-load orchestration for the Master Tag
 * Monitoring tab (Wave C-δ, specs/004-cml-phase2-master-tag-panel).
 *
 * Mirrors the singleton + AbortController pattern from `usePopupCard.js`
 * (Wave C-α). State is keyed by masterTagId / cmlRecordId / tmlPointId so a
 * single observable can serve multiple Master Tag views in sequence without
 * data bleeding between them; re-opening the same Master Tag immediately
 * surfaces cached results (no flicker).
 *
 * Implements:
 *   - FR-005   Lazy-load TmlPoints on CmlRecord row expand
 *   - FR-007   Lazy-load ThicknessReadings on TmlPoint row expand
 *   - R-9      AbortController cancellation on rapid collapse / re-expand
 *
 * Public API:
 *   loadCmlsForMasterTag(masterTagId, { includeArchived })
 *   loadTmlPointsForCml(cmlRecordId)
 *   loadReadingsForTmlPoint(tmlPointId)
 *   toggleCmlExpanded(cmlRecordId)
 *   toggleTmlPointExpanded(tmlPointId)
 *   abortAllInFlight()                    — call on tab unmount
 *
 * Design choice — Vue.observable vs Vuex (matches popup card precedent):
 *   Per CLAUDE.md Critical Pattern #5 (use mutations) + #6 (computed for state
 *   access), the project default is Vuex. We deviate here because Monitoring
 *   tab state is (a) transient — no persistence across navigation, (b)
 *   feature-local — only the Monitoring components read it, (c) keyed by
 *   parent entity ID so no cross-feature coordination, and (d) does not
 *   benefit from time-travel debugging given the short data lifespan. Same
 *   rationale documented in `usePopupCard.js`. Components MUST still wrap
 *   the observable in computed properties when reading from templates (per
 *   #6) — see `MonitoringTab.vue` for the pattern.
 *
 * Slice B scope note (T035): this composable is the data layer that T040-T046
 * (slice C) wire into MonitoringTab.vue's expand handlers. Slice B leaves the
 * `loadXxx` methods callable but does not yet bind them to expand events —
 * that wiring lands in T040 / T041.
 */

import Vue from 'vue';
import store from '@/store';
import httpService from '@/utils/httpService';
import { API_LIMITS } from '@/constants/apiLimits';
import { budgetCmlsForMarkers } from './useLevelColoring';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/**
 * Build the per-request headers object every CML v1 endpoint expects.
 * The CML controllers require `X-Facility-Id` strictly (400 if missing) —
 * older controllers don't, which is why httpService's static auth headers
 * don't include it. Scoped to this composable to keep the change small;
 * a project-wide interceptor is a Phase 3 cleanup. The header value is the
 * AVA `assetId` (same GUID, four names — see [[project_ava_asset_facility_vocab]]).
 *
 * @returns {{ 'X-Facility-Id': string }}
 */
function facilityHeaders() {
  return { 'X-Facility-Id': store.state.assetId };
}

// ─── Reactive singleton state ────────────────────────────────────────────────
// Vue.observable provides Vue-2-compatible reactivity for a plain object.
// All consumers (MonitoringTab.vue and its row components) read through
// computed properties; writes happen via the exported loader/toggle methods
// below (the "mutator" surface for this observable).
export const monitoringTabState = Vue.observable({
  // CmlRecord list per Master Tag — { masterTagId: Array<CmlRecordDto> }
  cmlsByMasterTag: {},
  loadingCmlsForMasterTag: {},      // { masterTagId: boolean }
  errorCmlsForMasterTag: {},        // { masterTagId: string|null }
  includeArchivedByMasterTag: {},   // { masterTagId: boolean } — FR-003

  // CmlRecord list per inspection — { inspectionId: Array<CmlRecordDto> }.
  // Populated by showInspectionCmlTmlMarkers: inspection markers must FETCH
  // their CMLs (the ForgeInspection in GET_EDITING_AMIS_TAG carries an empty
  // cmlRecords — only the getInspectionsByIds endpoint joins them). Cached here
  // so QuickSettings' syncCmlMarkers/syncTmlMarkers can re-render on toggle
  // without re-fetching. See getInspectionCmls().
  cmlsByInspection: {},
  loadingCmlsForInspection: {},
  errorCmlsForInspection: {},

  // Facility-scoped, authoritative master roots returned by the existing v1
  // Inspection metadata endpoint. Tree, markers, and Add Reading share this
  // cache so unrelated generic tag links never become request fan-out.
  masterTagIdsByInspection: {},
  loadingMasterTagIdsForInspection: {},
  errorMasterTagIdsForInspection: {},

  // Flat, list-only reading recap per inspection. This is deliberately separate
  // from the Tree caches below: List mode uses one paged inspection endpoint,
  // while Tree mode keeps its existing lazy CML → TML → Reading loaders.
  inspectionReadingsByInspection: {},
  loadingInspectionReadings: {},
  errorInspectionReadings: {},

  // TmlPoint list per CmlRecord — { cmlRecordId: Array<TmlPointDto> }
  tmlPointsByCml: {},
  loadingTmlPointsForCml: {},
  errorTmlPointsForCml: {},

  // ThicknessReading list per TmlPoint — { tmlPointId: Array<ThicknessReadingDto> }
  readingsByTmlPoint: {},
  loadingReadingsForTmlPoint: {},
  errorReadingsForTmlPoint: {},

  // Expand state — Vuetify v-expansion-panels uses arrays of indices, but we
  // also keep a flat lookup for fast lazy-load decision: { id: true } means
  // expanded. Components write through the toggle methods so the state stays
  // in sync with the lookup.
  expandedCmls: {},
  expandedTmlPoints: {},
});

// AbortControllers keyed by request type + parent ID, e.g. "cmls:abc-123" or
// "tmlpoints:xyz-456". A second loadXxx call for the same key cancels the
// prior one — typical "user toggled the panel quickly" race per R-9.
const abortControllers = new Map();

// In-flight de-duplication for TmlPoint loads, keyed by cmlRecordId → Promise.
// Several consumers (the 3D marker mixin, expand-on-click panels, the popup
// card) request the same CML's TmlPoints near-simultaneously. Without
// coalescing, each call would refreshAbortController() → abort the prior
// in-flight request and reissue — which, combined with the deep cacheSnapshot
// watcher in tagInfoCmlMarkersMixin re-firing on every write, degenerated into
// a one-at-a-time cancel/retry "marker storm" (28 aborted requests to load 7
// CMLs, ~4s instead of ~0.5s). Concurrent callers now share one request.
const inFlightTmlPoints = new Map();

// In-flight de-duplication for the master-tag TmlPoint BATCH load, keyed by
// masterTagId → Promise. On panel open, both loadAndShowCmlMarkers and the
// mixin's cacheSnapshot watcher (refreshMarkersFromCache) call
// loadTmlPointsForMasterTag almost simultaneously; without coalescing each
// would issue its own batch request (one aborting the other via the shared
// `tmlpoints-master:` abort key). Concurrent callers for the same master now
// share one request — see loadTmlPointsForMasterTag.
const _masterTmlPointsInFlight = new Map();

// Coalesce Inspection metadata reads across Tree, marker rendering, and the
// Add Reading panel. Unlike the flat List read model, this fetch carries no
// readings and is safe to reuse across those three consumers.
const _inspectionMasterTagIdsInFlight = new Map();

// Tree, marker rendering, and Add Reading can request the same inspection's
// CML visit scope at the same time. They must share one Promise: starting a
// second request would otherwise replace the shared AbortController and cancel
// the first consumer. The Promise identity also guards stale cache writes after
// teardown followed by a fast re-open of the same inspection.
const _inspectionCmlsInFlight = new Map();

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Detect whether an error from an axios call was caused by an AbortController.
 * Lifted from usePopupCard.js — same detection set works for the Monitoring
 * tab fetches because both consume the same httpService axios instance.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
function isAbortError(err) {
  if (!err) return false;
  if (err.name === 'AbortError' || err.name === 'CanceledError') return true;
  if (err.code === 'ERR_CANCELED') return true;
  if (err.message && /abort|cancel/i.test(err.message)) return true;
  return false;
}

/**
 * Replace or create the AbortController for a given fetch key, returning the
 * new controller. The prior controller (if any) is aborted before being
 * replaced.
 *
 * @param {string} key
 * @returns {AbortController}
 */
function refreshAbortController(key) {
  const prior = abortControllers.get(key);
  if (prior) {
    prior.abort();
    abortControllers.delete(key);
  }
  const controller = new AbortController();
  abortControllers.set(key, controller);
  return controller;
}

/**
 * Finalize a fetch's loading flag — handles the controller-cleanup and the
 * loading-flag write atomically. Replaces the previous "guard the cleanup
 * AND the flag clear behind controller equality" pattern, which could leave
 * the flag stuck true when HMR or rapid Master Tag switching produced an
 * order of events the equality check didn't anticipate (TRK-B-2).
 *
 * New invariant: after finalize, `loadingMap[id] === abortControllers.has(key)`.
 * Whichever fetch finishes (aborted, errored, or succeeded), the flag is
 * always recomputed from the canonical source — the controller map.
 *
 * @param {string} key                  — the abortControllers Map key
 * @param {AbortController} controller  — the controller for THIS in-flight fetch
 * @param {object} loadingMap           — observable map keyed by id
 * @param {string} id                   — masterTagId / cmlRecordId / tmlPointId
 */
function finalizeFetch(key, controller, loadingMap, id) {
  // Cleanup the map only if this fetch's controller is still the current one;
  // a newer call may have already replaced it (in which case its own finalize
  // owns the cleanup).
  if (abortControllers.get(key) === controller) {
    abortControllers.delete(key);
  }
  // Loading flag = "is there still an in-flight controller for this key?"
  // This is the canonical source — independent of which fetch finished.
  Vue.set(loadingMap, id, abortControllers.has(key));
}

/**
 * Normalize a CmlRecordDto's `conditionRating` field — the backend returns it
 * as an integer (e.g. 1 for Monitor) instead of the string enum name expected
 * by the wire contract (CmlConditionRating has [JsonStringEnumConverter] but
 * the ASP.NET Core serializer apparently isn't honoring it on the response
 * path). TRK-B-1: file a backend cleanup ticket and remove this shim once
 * the server emits strings consistently.
 *
 * Order matches the enum declaration in CmlConditionRating.cs:
 *   0 Acceptable, 1 Monitor, 2 ReInspect, 3 FfsRequired, 4 RepairRequired
 *
 * @param {object} cml
 * @returns {object} the SAME object, mutated in place — safe because we own
 *                   the DTO after the fetch (no shared references with prior
 *                   cache values).
 */
const CONDITION_RATING_ENUM = ['Acceptable', 'Monitor', 'ReInspect', 'FfsRequired', 'RepairRequired'];
function normalizeCmlRecord(cml) {
  if (!cml) return cml;
  if (typeof cml.conditionRating === 'number') {
    cml.conditionRating = CONDITION_RATING_ENUM[cml.conditionRating] ?? null;
  }
  return cml;
}

function normalizeInspectionReading(item) {
  if (!item) return item;
  if (typeof item.conditionRating === 'number') {
    item.conditionRating = CONDITION_RATING_ENUM[item.conditionRating] ?? null;
  }
  return item;
}

// ─── Public API: CmlRecord list ──────────────────────────────────────────────

async function fetchCmlsForMasterTag(
  masterTagId,
  { includeArchived = false, inspectionId = null, signal = null } = {},
) {
  const pageSize = API_LIMITS.MAX_CMLS_PAGE_SIZE;
  const allCmls = [];
  let page = 1;
  let total = Infinity;

  for (;;) {
    const pageResponse = await httpService.get({
      url: `/api/v1/master-tags/${masterTagId}/cmls`,
      params: {
        page,
        itemsPerPage: pageSize,
        ...(includeArchived ? { includeArchived: true } : {}),
        ...(inspectionId ? { inspectionId } : {}),
      },
      headers: facilityHeaders(),
      signal,
    });
    const pagePayload = (pageResponse && pageResponse.data) || {};
    const pageCmls = (pagePayload.items || []).map(normalizeCmlRecord);
    allCmls.push(...pageCmls);
    if (Number.isFinite(pagePayload.total)) total = pagePayload.total;
    if (pageCmls.length < pageSize || allCmls.length >= total) break;
    page += 1;
  }

  return { cmls: allCmls, pageCount: page };
}

/**
 * Fetch the CmlRecord list for a Master Tag (FR-002). Stores the result in
 * `monitoringTabState.cmlsByMasterTag[masterTagId]`. Subsequent calls for the
 * same ID overwrite the prior result — UI components should consume from the
 * state map, not the return value.
 *
 * @param {string} masterTagId
 * @param {{ includeArchived?: boolean }} [opts]
 * @returns {Promise<void>}
 */
export async function loadCmlsForMasterTag(masterTagId, opts = {}) {
  if (!masterTagId) {
    debugWarn('[useMonitoringTab] loadCmlsForMasterTag called with empty masterTagId — ignoring');
    return;
  }

  const key = `cmls:${masterTagId}`;
  const controller = refreshAbortController(key);
  const includeArchived = !!opts.includeArchived;

  Vue.set(monitoringTabState.loadingCmlsForMasterTag, masterTagId, true);
  Vue.set(monitoringTabState.errorCmlsForMasterTag, masterTagId, null);
  Vue.set(monitoringTabState.includeArchivedByMasterTag, masterTagId, includeArchived);

  try {
    // Page through ALL CMLs in batches so big masters show every list row + every
    // (worst-first budgeted) marker — not just the first 50-page. itemsPerPage is
    // capped server-side at API_LIMITS.MAX_CMLS_PAGE_SIZE; loop until a short page
    // or `total` is reached. Set cmlsByMasterTag ONCE at the end — the marker mixin
    // re-renders on every cmlsByMasterTag change, so progressive per-page writes
    // would storm clear/rebuild. (AVA-437)
    const { cmls, pageCount } = await fetchCmlsForMasterTag(masterTagId, {
      includeArchived,
      signal: controller.signal,
    });
    Vue.set(monitoringTabState.cmlsByMasterTag, masterTagId, cmls);
    debugLog(`[useMonitoringTab] loadCmlsForMasterTag(${masterTagId}) → ${cmls.length} rows across ${pageCount} page(s)`);
  } catch (err) {
    if (isAbortError(err)) {
      debugLog(`[useMonitoringTab] loadCmlsForMasterTag(${masterTagId}) aborted — discarding`);
      return;
    }
    debugError(`[useMonitoringTab] loadCmlsForMasterTag(${masterTagId}) failed`, err);
    Vue.set(
      monitoringTabState.errorCmlsForMasterTag,
      masterTagId,
      (err && err.message) || 'Failed to load CmlRecords.'
    );
  } finally {
    finalizeFetch(key, controller, monitoringTabState.loadingCmlsForMasterTag, masterTagId);
  }
}

/**
 * Fetch only the CMLs explicitly touched by one inspection's readings. This uses a separate cache
 * from the complete Master Tag list so opening an inspection never truncates the Master Tag panel.
 *
 * @param {string} inspectionId
 * @param {string[]} masterTagIds
 * @returns {Promise<Array>} the shared inspection-scoped CML result
 */
export function loadCmlsForInspection(inspectionId, masterTagIds) {
  const ids = [...new Set((Array.isArray(masterTagIds) ? masterTagIds : []).filter(Boolean))];
  if (!inspectionId || ids.length === 0) return Promise.resolve([]);

  const existing = _inspectionCmlsInFlight.get(inspectionId);
  if (existing) return existing;

  const key = `inspection-cmls:${inspectionId}`;
  const controller = refreshAbortController(key);
  let request;
  request = (async () => {
    Vue.set(monitoringTabState.loadingCmlsForInspection, inspectionId, true);
    Vue.set(monitoringTabState.errorCmlsForInspection, inspectionId, null);

    try {
      const masterCmlPages = await Promise.all(ids.map((masterTagId) =>
        fetchCmlsForMasterTag(masterTagId, {
          inspectionId,
          signal: controller.signal,
        })));

      // A teardown may clear the single-flight map before an HTTP adapter
      // settles its Promise. Only the still-current request may populate cache.
      if (controller.signal.aborted || _inspectionCmlsInFlight.get(inspectionId) !== request) {
        return monitoringTabState.cmlsByInspection[inspectionId] || [];
      }

      const cmls = masterCmlPages.flatMap((masterPage) => masterPage.cmls);
      Vue.set(monitoringTabState.cmlsByInspection, inspectionId, cmls);
      debugLog(`[useMonitoringTab] loadCmlsForInspection(${inspectionId}) → ${cmls.length} rows`);
      return cmls;
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) {
        debugLog(`[useMonitoringTab] loadCmlsForInspection(${inspectionId}) aborted — discarding`);
        return monitoringTabState.cmlsByInspection[inspectionId] || [];
      }
      debugError(`[useMonitoringTab] loadCmlsForInspection(${inspectionId}) failed`, err);
      Vue.set(
        monitoringTabState.errorCmlsForInspection,
        inspectionId,
        (err && err.message) || 'Failed to load inspection CMLs.',
      );
      return monitoringTabState.cmlsByInspection[inspectionId] || [];
    } finally {
      if (_inspectionCmlsInFlight.get(inspectionId) === request) {
        _inspectionCmlsInFlight.delete(inspectionId);
      }
      finalizeFetch(
        key,
        controller,
        monitoringTabState.loadingCmlsForInspection,
        inspectionId,
      );
    }
  })();

  _inspectionCmlsInFlight.set(inspectionId, request);
  return request;
}

/**
 * Fetch the flat Inspection Monitoring List read model. Unlike the Tree path,
 * this issues one request per API page and never fans out by Master/CML/TML.
 * The backend includes parent labels plus the immediately preceding reading,
 * so the client can render the complete visit recap without hydrating history.
 *
 * @param {string} inspectionId
 * @returns {Promise<Object|null>} fresh TmlPoints grouped by CML, or null when the batch failed
 */
export async function loadInspectionReadings(inspectionId) {
  if (!inspectionId) return;

  const key = `inspection-readings:${inspectionId}`;
  const controller = refreshAbortController(key);
  Vue.set(monitoringTabState.loadingInspectionReadings, inspectionId, true);
  Vue.set(monitoringTabState.errorInspectionReadings, inspectionId, null);

  try {
    const items = [];
    const itemsPerPage = API_LIMITS.MAX_INSPECTION_READINGS_PAGE_SIZE;
    let page = 1;
    let total = Infinity;

    while (items.length < total) {
      const response = await httpService.get({
        url: `/api/v1/inspections/${inspectionId}/thickness-readings`,
        params: { page, itemsPerPage },
        headers: facilityHeaders(),
        signal: controller.signal,
      });
      const payload = (response && response.data) || {};
      const pageItems = (payload.items || []).map(normalizeInspectionReading);
      items.push(...pageItems);
      total = Number.isFinite(payload.total) ? payload.total : items.length;

      if (pageItems.length === 0 || pageItems.length < itemsPerPage) break;
      page += 1;
    }

    Vue.set(monitoringTabState.inspectionReadingsByInspection, inspectionId, items);
    debugLog(`[useMonitoringTab] loadInspectionReadings(${inspectionId}) → ${items.length} rows across ${page} page(s)`);
  } catch (err) {
    if (isAbortError(err)) {
      debugLog(`[useMonitoringTab] loadInspectionReadings(${inspectionId}) aborted — discarding`);
      return;
    }
    debugError(`[useMonitoringTab] loadInspectionReadings(${inspectionId}) failed`, err);
    Vue.set(
      monitoringTabState.errorInspectionReadings,
      inspectionId,
      (err && err.message) || 'Failed to load inspection readings.',
    );
  } finally {
    finalizeFetch(
      key,
      controller,
      monitoringTabState.loadingInspectionReadings,
      inspectionId,
    );
  }
}

// ─── Public API: CmlRecord create / update ───────────────────────────────────

/**
 * POST a new CmlRecord under the given Master Tag. Returns the created
 * CmlRecordDto on success. On success, prepends the new record into the
 * cached list for that Master Tag so the UI refreshes without a full GET.
 *
 * Maps the form payload (UI shape) → CmlRecordCreateRequest (backend DTO).
 * FacilityId is sourced from store.state.assetId — same value the X-Facility-Id
 * header carries (per [[project_ava_asset_facility_vocab]]).
 *
 * @param {string} masterTagId
 * @param {object} cmlData — form payload from CmlAddEditPanel @save
 * @returns {Promise<object>} the created CmlRecordDto
 */
export async function createCmlForMasterTag(masterTagId, cmlData) {
  if (!masterTagId) {
    throw new Error('createCmlForMasterTag requires a masterTagId');
  }
  if (!cmlData) {
    throw new Error('createCmlForMasterTag requires cmlData');
  }

  const facilityId = store.state.assetId;
  const payload = {
    masterTagId,
    facilityId,
    linkedSubTagId: cmlData.linkedSubTagId || null,
    name: cmlData.name || null,
    cmlNo: cmlData.cmlNo || null,
    cmlType: cmlData.cmlType || null,
    inspectionStrategy: cmlData.inspectionStrategy || null,
    isoReference: cmlData.isoReference || null,
    point: cmlData.point || null,
    // Forge Viewer dbId captured at pick time (TRK-27). Persisted on POST so
    // the UI can later isolate / color the actual model element under the CML.
    forgeElementDbId: cmlData.forgeElementDbId ?? null,
    conditionRating: cmlData.conditionRating || null,
    designThickness: cmlData.designThickness ?? null,
    minimumThickness: cmlData.minimumThickness ?? null,
    nextInspectionDate: cmlData.nextInspectionDate || null,
    followUpDate: cmlData.followUpDate || null,
    observation: cmlData.observation || null,
    recommendation: cmlData.recommendation || null,
  };

  const response = await httpService.post({
    url: `/api/v1/master-tags/${masterTagId}/cmls`,
    data: payload,
    headers: facilityHeaders(),
  });
  const created = normalizeCmlRecord(response && response.data);
  debugLog(`[useMonitoringTab] createCmlForMasterTag(${masterTagId}) → ${created?.id} forgeElementDbId=${payload.forgeElementDbId}`);

  // Prepend into the cached list so the UI sees the new row immediately.
  const list = monitoringTabState.cmlsByMasterTag[masterTagId] || [];
  Vue.set(monitoringTabState.cmlsByMasterTag, masterTagId, [created, ...list]);
  return created;
}

/**
 * PUT an existing CmlRecord. Carries the RowVersion the caller observed at
 * load-time so the backend enforces optimistic concurrency (409 Conflict on
 * stale write per FR-026). The 409 case bubbles to the caller with a flag
 * so the UI can decide whether to refetch + offer reload.
 *
 * On success, replaces the matching row in the cached list for the parent
 * Master Tag so the UI re-renders the updated values.
 *
 * @param {string} masterTagId — parent (for cache update)
 * @param {string} cmlRecordId
 * @param {object} cmlData — form payload from CmlAddEditPanel @save (must have id + rowVersion)
 * @returns {Promise<object>} the updated CmlRecordDto
 */
export async function updateCml(masterTagId, cmlRecordId, cmlData) {
  if (!cmlRecordId) {
    throw new Error('updateCml requires a cmlRecordId');
  }
  if (!cmlData || !cmlData.rowVersion) {
    throw new Error('updateCml requires cmlData.rowVersion (FR-026 optimistic concurrency)');
  }

  const payload = {
    rowVersion: cmlData.rowVersion,
    linkedSubTagId: cmlData.linkedSubTagId || null,
    name: cmlData.name || null,
    cmlNo: cmlData.cmlNo || null,
    cmlType: cmlData.cmlType || null,
    inspectionStrategy: cmlData.inspectionStrategy || null,
    isoReference: cmlData.isoReference || null,
    point: cmlData.point || null,
    // Forge Viewer dbId — updated alongside Point when the user re-picks the
    // 3D anchor. Null clears a previously stored dbId.
    forgeElementDbId: cmlData.forgeElementDbId ?? null,
    conditionRating: cmlData.conditionRating || null,
    designThickness: cmlData.designThickness ?? null,
    minimumThickness: cmlData.minimumThickness ?? null,
    nextInspectionDate: cmlData.nextInspectionDate || null,
    followUpDate: cmlData.followUpDate || null,
    observation: cmlData.observation || null,
    recommendation: cmlData.recommendation || null,
  };

  const response = await httpService.put({
    url: `/api/v1/cmls/${cmlRecordId}`,
    data: payload,
    headers: facilityHeaders(),
  });
  const updated = normalizeCmlRecord(response && response.data);
  debugLog(`[useMonitoringTab] updateCml(${cmlRecordId}) → ok forgeElementDbId=${payload.forgeElementDbId}`);

  // Merge over the prior cached row — defensive against the same partial-DTO
  // class of bug that produced TRK-110 on TmlPoint. Edit-PUT can't invalidate
  // reading-derived rollups (worstCR / minRL / minWT / lastInspectionDate)
  // because the user is editing config-only fields; if the backend response
  // omits any of those, we keep the cached snapshot rather than blank out the
  // pill UI until the next refresh.
  const list = monitoringTabState.cmlsByMasterTag[masterTagId] || [];
  const next = list.map((c) => (c.id === cmlRecordId ? { ...c, ...updated } : c));
  Vue.set(monitoringTabState.cmlsByMasterTag, masterTagId, next);
  return updated;
}

/**
 * Soft-delete a CmlRecord (DELETE /api/v1/cmls/{cmlRecordId}). The backend
 * treats this as a C-4 lifecycle UPDATE and REQUIRES the RowVersion via the
 * `If-Match` header (FR-026 optimistic concurrency) — unlike the PUT path,
 * which carries rowVersion in the body. The server trims quotes + base64-
 * decodes the header value (CmlsController.TryParseIfMatch), and `cml.rowVersion`
 * is already the base64 token, so it travels verbatim. A stale token yields a
 * 409 ({ error: 'rowVersionMismatch', message }) — the caller surfaces that
 * message so the user can refresh + retry.
 *
 * On success, removes the row from the cached list for the parent Master Tag.
 * The 3D marker layer watches this cache snapshot, so the marker disappears
 * with the row. The CML's TmlPoints are cascade-soft-deleted server-side, so
 * the stale child cache is evicted too (a future expand re-fetches).
 *
 * @param {string} masterTagId — parent (for cache update)
 * @param {string} cmlRecordId
 * @param {string} rowVersion — base64 token from the cached CML (cml.rowVersion)
 * @returns {Promise<void>}
 */
export async function deleteCml(masterTagId, cmlRecordId, rowVersion) {
  if (!cmlRecordId) {
    throw new Error('deleteCml requires a cmlRecordId');
  }
  if (!rowVersion) {
    throw new Error('deleteCml requires rowVersion (If-Match / FR-026 optimistic concurrency)');
  }

  await httpService.delete({
    url: `/api/v1/cmls/${cmlRecordId}`,
    headers: { ...facilityHeaders(), 'If-Match': rowVersion },
  });
  debugLog(`[useMonitoringTab] deleteCml(${cmlRecordId}) → ok`);

  // Drop the row from the cached list so it (and its 3D marker) disappears.
  if (masterTagId) {
    const list = monitoringTabState.cmlsByMasterTag[masterTagId] || [];
    Vue.set(
      monitoringTabState.cmlsByMasterTag,
      masterTagId,
      list.filter((c) => c.id !== cmlRecordId),
    );
  }
  // Evict the cascade-soft-deleted child TmlPoints so a re-expand re-fetches
  // rather than rendering ghost rows / markers.
  if (monitoringTabState.tmlPointsByCml[cmlRecordId]) {
    Vue.delete(monitoringTabState.tmlPointsByCml, cmlRecordId);
  }
}

// ─── Public API: TmlPoint create / update ───────────────────────────────────

/**
 * POST a new TmlPoint under the given CmlRecord. Returns the created
 * TmlPointDto on success. Prepends into the cached list for the parent CML
 * so the UI refreshes without a full GET.
 *
 * Backend route: POST /api/v1/cmls/{cmlRecordId}/tml-points
 * No RowVersion is needed for TmlPoint per FR-028 (single-writer-update);
 * the permission gate is the only concurrency control.
 *
 * @param {string} cmlRecordId
 * @param {object} tmlPointData — form payload from TmlPointAddEditPanel @save
 * @returns {Promise<object>} the created TmlPointDto
 */
export async function createTmlPointForCml(cmlRecordId, tmlPointData) {
  if (!cmlRecordId) {
    throw new Error('createTmlPointForCml requires a cmlRecordId');
  }
  if (!tmlPointData) {
    throw new Error('createTmlPointForCml requires tmlPointData');
  }

  const payload = {
    cmlRecordId,
    position: tmlPointData.position || null,
    point: tmlPointData.point || null,
    // Forge Viewer dbId from the TmlPoint pick. Persisted so per-TmlPoint
    // geometry isolation can resolve the right model element later (TRK-27).
    forgeElementDbId: tmlPointData.forgeElementDbId ?? null,
    linkedSubTagId: tmlPointData.linkedSubTagId || null,
  };

  const response = await httpService.post({
    url: `/api/v1/cmls/${cmlRecordId}/tml-points`,
    data: payload,
    headers: facilityHeaders(),
  });
  const created = response && response.data;
  debugLog(`[useMonitoringTab] createTmlPointForCml(${cmlRecordId}) → ${created?.id} forgeElementDbId=${payload.forgeElementDbId}`);

  // Prepend into the cached list so the UI sees the new point immediately.
  const list = monitoringTabState.tmlPointsByCml[cmlRecordId] || [];
  Vue.set(monitoringTabState.tmlPointsByCml, cmlRecordId, [created, ...list]);
  return created;
}

/**
 * PUT an existing TmlPoint. No RowVersion per FR-028 single-writer-update;
 * the permission gate is the only concurrency control (no 409 path).
 *
 * Backend route: PUT /api/v1/tml-points/{tmlPointId}
 *
 * On success, replaces the matching row in the cached list for the parent
 * CmlRecord so the UI re-renders the updated values.
 *
 * @param {string} cmlRecordId — parent (for cache update)
 * @param {string} tmlPointId
 * @param {object} tmlPointData — form payload from TmlPointAddEditPanel @save
 * @returns {Promise<object>} the updated TmlPointDto
 */
export async function updateTmlPoint(cmlRecordId, tmlPointId, tmlPointData) {
  if (!tmlPointId) {
    throw new Error('updateTmlPoint requires a tmlPointId');
  }
  if (!tmlPointData) {
    throw new Error('updateTmlPoint requires tmlPointData');
  }

  const payload = {
    position: tmlPointData.position || null,
    point: tmlPointData.point || null,
    // Forge Viewer dbId — updated alongside Point on re-pick.
    forgeElementDbId: tmlPointData.forgeElementDbId ?? null,
    linkedSubTagId: tmlPointData.linkedSubTagId || null,
  };

  const response = await httpService.put({
    url: `/api/v1/tml-points/${tmlPointId}`,
    data: payload,
    headers: facilityHeaders(),
  });
  const updated = response && response.data;
  debugLog(`[useMonitoringTab] updateTmlPoint(${tmlPointId}) → ok forgeElementDbId=${payload.forgeElementDbId}`);

  // Merge over the prior cached row instead of replacing wholesale — the PUT
  // response carries only writable fields (position, point, linkedSubTagId)
  // and omits derived ones (latestReadingValue / latestReadingDate, etc.)
  // that are computed on the GET-list path. Wholesale replace would blank
  // those out until the next refresh (TRK-110). Edit-PUT can't invalidate
  // reading-derived fields, so preserving them from the cached snapshot is
  // safe; any field the server does return wins via the spread order.
  if (cmlRecordId) {
    const list = monitoringTabState.tmlPointsByCml[cmlRecordId] || [];
    const next = list.map((tp) => (tp.id === tmlPointId ? { ...tp, ...updated } : tp));
    Vue.set(monitoringTabState.tmlPointsByCml, cmlRecordId, next);
  }
  return updated;
}

/**
 * Soft-delete a TmlPoint (DELETE /api/v1/tml-points/{tmlPointId}). No
 * RowVersion per FR-028 single-writer-update — the permission gate is the only
 * concurrency control (no 409 path, no If-Match header). On success, removes
 * the row from the cached list for the parent CmlRecord.
 *
 * The backend recomputes the parent CML's cached metrics (worstCR / minRL /
 * minWT / conditionRating) in the same delete transaction, so the caller
 * should follow up with `refreshCmlRecord(masterTagId, cmlRecordId)` to pull
 * the new values into the parent row.
 *
 * @param {string} cmlRecordId — parent (for cache update)
 * @param {string} tmlPointId
 * @returns {Promise<void>}
 */
export async function deleteTmlPoint(cmlRecordId, tmlPointId) {
  if (!tmlPointId) {
    throw new Error('deleteTmlPoint requires a tmlPointId');
  }

  await httpService.delete({
    url: `/api/v1/tml-points/${tmlPointId}`,
    headers: facilityHeaders(),
  });
  debugLog(`[useMonitoringTab] deleteTmlPoint(${tmlPointId}) → ok`);

  // Drop the row from the cached list so it (and its 3D marker) disappears.
  if (cmlRecordId) {
    const list = monitoringTabState.tmlPointsByCml[cmlRecordId] || [];
    Vue.set(
      monitoringTabState.tmlPointsByCml,
      cmlRecordId,
      list.filter((tp) => tp.id !== tmlPointId),
    );
  }
}

// ─── Public API: TmlPoint list ───────────────────────────────────────────────

/**
 * Fetch the TmlPoint list for a CmlRecord (FR-005). Stores the result in
 * `monitoringTabState.tmlPointsByCml[cmlRecordId]`.
 *
 * @param {string} cmlRecordId
 * @returns {Promise<void>}
 */
export function loadTmlPointsForCml(cmlRecordId) {
  if (!cmlRecordId) {
    debugWarn('[useMonitoringTab] loadTmlPointsForCml called with empty cmlRecordId — ignoring');
    return Promise.resolve();
  }

  // Coalesce onto the in-flight request for this CML, if one exists, instead of
  // aborting + reissuing it (the marker-storm cause — see inFlightTmlPoints).
  const existing = inFlightTmlPoints.get(cmlRecordId);
  if (existing) return existing;

  // Plain (non-async) wrapper so the object we store, attach `.finally` to, and
  // identity-check below are all the SAME promise reference.
  const promise = _fetchTmlPointsForCml(cmlRecordId).finally(() => {
    // Identity guard (mirrors finalizeFetch): clear the entry only if it still
    // points to THIS promise. A request aborted by a collapse can settle AFTER
    // a re-expand has already stored a fresh promise — without this guard, the
    // stale settle would wipe the fresh entry and silently re-enable
    // double-fetching. Paired with the delete in cancelLoadTmlPointsForCml.
    if (inFlightTmlPoints.get(cmlRecordId) === promise) {
      inFlightTmlPoints.delete(cmlRecordId);
    }
  });
  inFlightTmlPoints.set(cmlRecordId, promise);
  return promise;
}

/**
 * Internal: the actual TmlPoint fetch. Always wrapped by loadTmlPointsForCml's
 * de-dup layer — do not call directly (it would bypass coalescing).
 *
 * @param {string} cmlRecordId
 * @returns {Promise<void>}
 */
async function _fetchTmlPointsForCml(cmlRecordId) {
  const key = `tmlpoints:${cmlRecordId}`;
  const controller = refreshAbortController(key);

  Vue.set(monitoringTabState.loadingTmlPointsForCml, cmlRecordId, true);
  Vue.set(monitoringTabState.errorTmlPointsForCml, cmlRecordId, null);

  try {
    const response = await httpService.get({
      url: `/api/v1/cmls/${cmlRecordId}/tml-points`,
      headers: facilityHeaders(),
      signal: controller.signal,
    });
    const items = (response && response.data && response.data.items) || [];
    Vue.set(monitoringTabState.tmlPointsByCml, cmlRecordId, items);
    debugLog(`[useMonitoringTab] loadTmlPointsForCml(${cmlRecordId}) → ${items.length} rows`);
  } catch (err) {
    if (isAbortError(err)) {
      debugLog(`[useMonitoringTab] loadTmlPointsForCml(${cmlRecordId}) aborted — discarding`);
      return;
    }
    debugError(`[useMonitoringTab] loadTmlPointsForCml(${cmlRecordId}) failed`, err);
    Vue.set(
      monitoringTabState.errorTmlPointsForCml,
      cmlRecordId,
      (err && err.message) || 'Failed to load TmlPoints.'
    );
  } finally {
    finalizeFetch(key, controller, monitoringTabState.loadingTmlPointsForCml, cmlRecordId);
  }
}

/**
 * Internal: the actual master-tag batch TmlPoint fetch. Always wrapped by
 * `loadTmlPointsForMasterTag`'s single-flight de-dup layer — do not call
 * directly (it would bypass coalescing). See that wrapper for the full
 * contract (seed-all-to-`[]` rationale, no loading/error map, etc.).
 *
 * @param {string} masterTagId
 * @returns {Promise<void>}
 */
async function _loadTmlPointsForMasterTagInner(masterTagId) {
  // Distinct abort key from the per-CML loader's `tmlpoints:${cmlRecordId}` so a
  // fast master-switch cancels THIS batch fetch cleanly without touching any
  // in-flight per-CML request (R-9 — same cancel-on-rapid-switch posture).
  const key = `tmlpoints-master:${masterTagId}`;
  const controller = refreshAbortController(key);

  try {
    const response = await httpService.get({
      url: `/api/v1/master-tags/${masterTagId}/tml-points`,
      headers: facilityHeaders(),
      signal: controller.signal,
    });
    // Same PagedResult envelope every other loader reads: { items, total, … }.
    const items = (response && response.data && response.data.items) || [];

    // Group the flat TmlPoint list by parent cmlRecordId.
    const grouped = {};
    items.forEach((tp) => {
      const cmlId = tp && tp.cmlRecordId;
      if (!cmlId) return;
      if (!grouped[cmlId]) grouped[cmlId] = [];
      grouped[cmlId].push(tp);
    });

    // Pass 1 — seed EVERY cached CML (the load-bearing `[]` for zero-TML CMLs,
    // so refreshMarkersFromCache won't re-fetch them per-CML). One synchronous
    // pass after the response resolves.
    const cmls = monitoringTabState.cmlsByMasterTag[masterTagId] || [];
    cmls.forEach((cml) => {
      Vue.set(monitoringTabState.tmlPointsByCml, cml.id, grouped[cml.id] || []);
    });

    // Pass 2 — refresh every grouped CML the response knows about, including
    // inspection-only CMLs absent from cmlsByMasterTag. The batch response is
    // authoritative; retaining a truthy old cache here can repaint deleted TMLs.
    Object.keys(grouped).forEach((cmlId) => {
      Vue.set(monitoringTabState.tmlPointsByCml, cmlId, grouped[cmlId]);
    });

    debugLog(`[useMonitoringTab] loadTmlPointsForMasterTag(${masterTagId}) → ${items.length} TmlPoints across ${Object.keys(grouped).length} CMLs (seeded ${cmls.length} cached CMLs)`);
    return grouped;
  } catch (err) {
    // Mirror loadAndShowCmlMarkers' silent-failure posture: never throw — markers
    // just won't render this time. Aborts are expected on rapid master-switch.
    if (isAbortError(err)) {
      debugLog(`[useMonitoringTab] loadTmlPointsForMasterTag(${masterTagId}) aborted — discarding`);
      return null;
    }
    debugError(`[useMonitoringTab] loadTmlPointsForMasterTag(${masterTagId}) failed`, err);
    return null;
  } finally {
    // Cleanup the controller only if this fetch's controller is still current —
    // a newer call may have already replaced it via refreshAbortController.
    // (No loading-flag recompute here — there is no master-level loading map.)
    if (abortControllers.get(key) === controller) {
      abortControllers.delete(key);
    }
  }
}

/**
 * Batch counterpart to `loadTmlPointsForCml` (AVA-437). Fetches EVERY TmlPoint
 * for a Master Tag in ONE request — `GET /api/v1/master-tags/{id}/tml-points` —
 * then fans the result out into `monitoringTabState.tmlPointsByCml` keyed by
 * each point's `cmlRecordId`. This eliminates the per-CML N+1 fan-out that
 * `loadAndShowCmlMarkers` previously incurred on panel open (one tml-points
 * request per CML, throttled but still a storm on many-CML masters — see the
 * marker-storm 429/CORS history).
 *
 * CRITICAL — the seed-all-to-`[]` step is load-bearing, NOT defensive padding:
 * `refreshMarkersFromCache` (tagInfoCmlMarkersMixin) re-fetches per-CML any CML
 * where `!monitoringTabState.tmlPointsByCml[cml.id]`. If a CML that legitimately
 * has ZERO TmlPoints were left `undefined` here, the very next cache tick would
 * re-issue a per-CML `loadTmlPointsForCml` for it — reintroducing the exact N+1
 * storm this batch endpoint removes. So we explicitly seed EVERY cached CML to
 * `grouped[cml.id] || []` (the `[]` is the truthy-cache marker that says "loaded,
 * none"), then defensively add any grouped cmlRecordId the cache list didn't know
 * about.
 *
 * Deliberately carries NO loading/error flag writes: `loadingTmlPointsForCml` and
 * `errorTmlPointsForCml` are keyed by cmlRecordId, so writing a masterTagId key
 * into them would corrupt the per-CML state other code reads. There is no
 * master-level loading/error map by design (the mixin's silent-failure posture
 * covers the UX — markers just won't render this time).
 *
 * Reads the master's CML list from `cmlsByMasterTag[masterTagId]` (a pure reader;
 * it does NOT fetch CMLs). The mixin awaits `loadCmlsForMasterTag` before calling
 * this, so the list is populated when we read it.
 *
 * @param {string} masterTagId
 * @returns {Promise<Object|null>} fresh TmlPoints grouped by CML, or null when the batch failed
 */
export function loadTmlPointsForMasterTag(masterTagId) {
  if (!masterTagId) {
    debugWarn('[useMonitoringTab] loadTmlPointsForMasterTag called with empty masterTagId — ignoring');
    return Promise.resolve(null);
  }
  // Single-flight per master: loadAndShowCmlMarkers and the mixin's cacheSnapshot
  // watcher (refreshMarkersFromCache) both call this almost simultaneously on panel
  // open; without dedup they'd each fire a batch request (one aborting the other).
  // Share one in-flight promise so exactly one request goes out.
  const existing = _masterTmlPointsInFlight.get(masterTagId);
  if (existing) return existing;
  const promise = _loadTmlPointsForMasterTagInner(masterTagId)
    .finally(() => { _masterTmlPointsInFlight.delete(masterTagId); });
  _masterTmlPointsInFlight.set(masterTagId, promise);
  return promise;
}

// ─── Inspection marker rendering (CML + TML, 3D + 360) ───────────────────────

/**
 * Hydrate a CmlRecord array with its TmlPoints from the monitoring cache.
 * `CmlRecordDto` doesn't carry nested TmlPoints (they live in a sibling
 * endpoint), so callers that want TML markers must splice them on for the
 * render pass. Render-only: returns NEW objects, never mutates the inputs
 * (immutability — see clean-code rules).
 *
 * @param {Array} cmls
 * @returns {Array} new CmlRecord copies, each with a `tmlPoints` array
 */
export function hydrateCmlsWithTmlPoints(cmls) {
  if (!Array.isArray(cmls)) return [];
  return cmls.map((cml) => ({
    ...cml,
    tmlPoints: monitoringTabState.tmlPointsByCml[cml.id] || [],
  }));
}

/**
 * Read the cached CMLs for an inspection (populated by
 * showInspectionCmlTmlMarkers). Used by QuickSettings' visibility toggle so it
 * can re-render without re-fetching. Returns [] when not yet fetched.
 *
 * @param {string} inspectionId
 * @returns {Array} CmlRecordDto[]
 */
export function getInspectionCmls(inspectionId) {
  if (!inspectionId) return [];
  return monitoringTabState.cmlsByInspection[inspectionId] || [];
}

/**
 * Resolve an inspection's master tag id(s). CMLs anchor to the EQUIPMENT master
 * tag, NOT the inspection — the master id supplies the query scope, while
 * `inspectionId` filters that scope to CMLs touched by this visit's readings.
 * Mirrors InspectionMonitoringTab's `masterTagIds` computed: an
 * inspection may span multiple masters, and the DTO uses several field
 * spellings depending on the entry path.
 *
 * @param {Object} inspection
 * @returns {string[]} master tag ids (possibly empty before hydration)
 */
function projectedInspectionMasterTagIds(inspection) {
  if (!inspection) return [];

  const ids = [];
  const seen = new Set();
  const add = (value) => {
    if (!value) return;
    const id = String(value).trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  const addMany = (values) => {
    if (Array.isArray(values)) values.forEach(add);
  };

  // Only explicitly typed master fields are trusted here. Generic linkedTags
  // collections can contain Anomaly/Inspection/etc. and must never be treated
  // as master roots client-side. The v1 metadata endpoint validates linked and
  // reading-derived candidates against persisted IsMaster + facility state.
  addMany(inspection.masterTagIds);
  addMany(inspection.MasterTagIds);
  add(inspection.masterTagId);
  add(inspection.MasterTagId);
  add(inspection.masterTag?.id);

  return ids;
}

/**
 * Read the authoritative cached list when available, otherwise use only the
 * inspection's explicit master fields as a fail-closed pre-fetch fallback.
 */
export function resolveInspectionMasterTagIds(inspection) {
  const inspectionId = inspection?.id;
  if (inspectionId && Object.prototype.hasOwnProperty.call(
    monitoringTabState.masterTagIdsByInspection,
    inspectionId,
  )) {
    return monitoringTabState.masterTagIdsByInspection[inspectionId] || [];
  }
  return projectedInspectionMasterTagIds(inspection);
}

/**
 * Fetch the existing facility-scoped v1 Inspection metadata projection once.
 * The backend supplies only persisted, live IsMaster roots for this facility;
 * it unions the primary anchor, real master-link endpoints, and reading-derived
 * CML masters without returning or traversing the readings themselves.
 *
 * @param {string} inspectionId
 * @param {{ force?: boolean }} options
 * @returns {Promise<string[]>}
 */
export function loadInspectionMasterTagIds(inspectionId, { force = false } = {}) {
  if (!inspectionId) return Promise.resolve([]);

  const cached = monitoringTabState.masterTagIdsByInspection[inspectionId];
  if (!force && Object.prototype.hasOwnProperty.call(
    monitoringTabState.masterTagIdsByInspection,
    inspectionId,
  )) {
    return Promise.resolve(cached || []);
  }

  const existing = _inspectionMasterTagIdsInFlight.get(inspectionId);
  if (existing) return existing;

  const key = `inspection-masters:${inspectionId}`;
  const controller = refreshAbortController(key);
  let request;
  request = (async () => {
    Vue.set(monitoringTabState.loadingMasterTagIdsForInspection, inspectionId, true);
    Vue.set(monitoringTabState.errorMasterTagIdsForInspection, inspectionId, null);
    try {
      const response = await httpService.get({
        url: `/api/v1/inspections/${inspectionId}`,
        headers: facilityHeaders(),
        signal: controller.signal,
      });
      if (controller.signal.aborted
        || _inspectionMasterTagIdsInFlight.get(inspectionId) !== request) {
        return monitoringTabState.masterTagIdsByInspection[inspectionId] || [];
      }
      const ids = projectedInspectionMasterTagIds((response && response.data) || {});
      Vue.set(monitoringTabState.masterTagIdsByInspection, inspectionId, ids);
      return ids;
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) {
        debugLog(`[useMonitoringTab] loadInspectionMasterTagIds(${inspectionId}) aborted — discarding`);
        return monitoringTabState.masterTagIdsByInspection[inspectionId] || [];
      }
      debugError(`[useMonitoringTab] loadInspectionMasterTagIds(${inspectionId}) failed`, err);
      Vue.set(
        monitoringTabState.errorMasterTagIdsForInspection,
        inspectionId,
        (err && err.message) || 'Failed to resolve inspection Master Tags.',
      );
      return monitoringTabState.masterTagIdsByInspection[inspectionId] || [];
    } finally {
      if (_inspectionMasterTagIdsInFlight.get(inspectionId) === request) {
        _inspectionMasterTagIdsInFlight.delete(inspectionId);
      }
      finalizeFetch(
        key,
        controller,
        monitoringTabState.loadingMasterTagIdsForInspection,
        inspectionId,
      );
    }
  })();

  _inspectionMasterTagIdsInFlight.set(inspectionId, request);
  return request;
}

function isInspectionStillActive(inspectionId) {
  return store.getters['GET_EDITING_AMIS_TAG']?.id === inspectionId;
}

/**
 * Render CML + TML markers (3D spheres + 360 hotspots) for an inspection.
 *
 * Inspection-side parallel to `tagInfoCmlMarkersMixin`. The critical fact: CMLs
 * anchor to the inspection's MASTER TAG(s), not the inspection itself — the
 * ForgeInspection in GET_EDITING_AMIS_TAG carries an empty `cmlRecords`. So we
 * resolve the inspection's master tag id(s), fetch only CMLs with a reading
 * attached to this inspection, then:
 *   1. render CML markers, then
 *   2. fetch each CML's TmlPoints (sibling endpoint) and render TML markers.
 *
 * The resolved CMLs are cached per inspection id (`cmlsByInspection`) so the
 * QuickSettings visibility toggle can re-render without re-loading.
 *
 * Visibility stays gated downstream by `showCml`/`showTml` inside the
 * forgeService marker functions — this only supplies the trigger + the data.
 *
 * NOTE: requires a valid API session (R-accessToken) — the CML + TmlPoint loads
 * both hit the API.
 *
 * @param {Object} forgeService - the ForgeViewerService instance
 * @param {Object} inspection - must carry an `id` + a resolvable master tag id
 * @returns {Promise<void>}
 */
export async function showInspectionCmlTmlMarkers(forgeService, inspection) {
  if (!forgeService?.showCmlPointMarkers) return;

  const inspectionId = inspection?.id;
  if (!inspectionId) return;

  const masterTagIds = await loadInspectionMasterTagIds(inspectionId);
  if (!isInspectionStillActive(inspectionId)) {
    debugLog(`[useMonitoringTab] inspection ${inspectionId} is no longer active — skipping stale marker work`);
    return;
  }
  if (masterTagIds.length === 0) {
    // No masters resolved — this is normal on the sparse pre-hydration watcher
    // fire (OPEN_TAG commits a sparse tag, then re-commits the hydrated one).
    // Clear any markers left from a previously-shown inspection so they don't
    // linger, then bail. The keys dump diagnoses the case where it stays empty
    // even after hydration (i.e. a genuinely missing master tag field).
    debugLog(`[useMonitoringTab] inspection ${inspectionId}: no master tag ids yet (keys=${Object.keys(inspection || {}).join(',')})`);
    forgeService.clearCmlPointMarkers?.();
    forgeService.clearTmlPointMarkers?.();
    return;
  }

  await loadCmlsForInspection(inspectionId, masterTagIds);

  if (!isInspectionStillActive(inspectionId)) {
    debugLog(`[useMonitoringTab] inspection ${inspectionId} is no longer active — skipping stale CML render`);
    return;
  }

  const cmls = monitoringTabState.cmlsByInspection[inspectionId] || [];
  debugLog(`[useMonitoringTab] inspection ${inspectionId}: ${cmls.length} linked CMLs across ${masterTagIds.length} master tag(s) [${masterTagIds.join(', ')}]`);

  // Worst-first marker budget: a master/inspection with thousands of CMLs would
  // otherwise render thousands of textured labels. Budget ONCE and feed the SAME
  // subset to BOTH the CML render and the TML hydrate below, so the two marker
  // layers stay in lockstep. The cache above stays FULL (QuickSettings re-budgets
  // from it); only the render subset is capped. (AVA-437)
  const budgeted = budgetCmlsForMarkers(cmls);
  if (budgeted.capped) {
    debugLog(`[useMonitoringTab] inspection ${inspectionId}: marker budget — showing ${budgeted.shown} of ${budgeted.total} CMLs (worst first)`);
  }

  // CML markers (3D + 360) from the master tags' records.
  forgeService.showCmlPointMarkers({ cmlRecords: budgeted.cmls });

  if (cmls.length === 0 || !forgeService.showTmlPointMarkers) return;

  // TmlPoints live in a sibling endpoint. Batch per master tag (one request each
  // via the single-flight loadTmlPointsForMasterTag) instead of one request per
  // CML — mirrors the master-tag panel path (tagInfoCmlMarkersMixin) and removes
  // the per-CML N+1 fan-out that previously fired on this inspection path. The
  // batch seeds every cached CML's entry in tmlPointsByCml, which
  // hydrateCmlsWithTmlPoints then reads. (AVA-437)
  const groupedByMaster = new Map(await Promise.all(masterTagIds.map(async (mtId) => [
    mtId,
    await loadTmlPointsForMasterTag(mtId).catch(() => null),
  ])));

  if (!isInspectionStillActive(inspectionId)) {
    debugLog(`[useMonitoringTab] inspection ${inspectionId} is no longer active — skipping stale TML render`);
    return;
  }

  // Inspection CMLs intentionally live in cmlsByInspection, not the ordinary
  // Master Tag cache seeded inside the batch loader. Apply each successful
  // batch snapshot to this render subset as well, explicitly writing [] for a
  // CML with zero current TMLs. Failed batches leave prior cache untouched and
  // produce no false "loaded empty" state.
  budgeted.cmls.forEach((cml) => {
    const masterTagId = cml.masterTagId || cml.MasterTagId;
    const grouped = groupedByMaster.get(masterTagId);
    if (grouped) {
      Vue.set(monitoringTabState.tmlPointsByCml, cml.id, grouped[cml.id] || []);
    }
  });

  forgeService.showTmlPointMarkers({ cmlRecords: hydrateCmlsWithTmlPoints(budgeted.cmls) });
}

// ─── Public API: ThicknessReading create ─────────────────────────────────────

/**
 * POST a new ThicknessReading under the given TmlPoint. After success:
 *   1. Prepend the created row into the readings cache so the timeline
 *      reflects the new entry without a re-fetch.
 *   2. The caller (MonitoringTab) is expected to follow up with
 *      `refreshCmlRecord(masterTagId, cmlRecordId)` + `loadTmlPointsForCml`
 *      so the parent CML's recomputed metrics (worstCR / minRL / minWT /
 *      conditionRating / lastInspectionDate) AND the TmlPoint's derived
 *      latestReadingValue/Date refresh in the UI.
 *
 * Backend route: POST /api/v1/tml-points/{tmlPointId}/thickness-readings
 * Append-only at the DB layer via TR_ThicknessReadings_NoUpdate; this
 * helper only handles the INSERT path.
 *
 * @param {string} tmlPointId
 * @param {object} readingData — form payload from ThicknessReadingAddPanel @save
 * @returns {Promise<object>} the created ThicknessReadingDto
 */
export async function createThicknessReading(tmlPointId, readingData) {
  if (!tmlPointId) {
    throw new Error('createThicknessReading requires a tmlPointId');
  }
  if (!readingData) {
    throw new Error('createThicknessReading requires readingData');
  }

  const payload = {
    tmlPointId,
    // ForgeInspectionId nullable: null = historical/standalone per FR-013.
    forgeInspectionId: readingData.forgeInspectionId || null,
    value: Number(readingData.value),
    // The form sends a yyyy-MM-dd string; the backend's DateTime model
    // binder accepts ISO date strings natively and parses as midnight UTC.
    readingDate: readingData.readingDate,
  };

  const response = await httpService.post({
    url: `/api/v1/tml-points/${tmlPointId}/thickness-readings`,
    data: payload,
    headers: facilityHeaders(),
  });
  const created = response && response.data;
  debugLog(`[useMonitoringTab] createThicknessReading(${tmlPointId}) → ${created?.id}`);

  // Prepend into the cached readings list. The Monitoring tab's reading
  // timeline orders by date descending; a newly-saved reading is typically
  // the most recent so prepending is correct in the common case. If a user
  // backfills a reading dated months ago, the order will be off until the
  // next refresh — acceptable v1 trade-off.
  const list = monitoringTabState.readingsByTmlPoint[tmlPointId] || [];
  Vue.set(monitoringTabState.readingsByTmlPoint, tmlPointId, [created, ...list]);
  return created;
}

/**
 * GET a single CmlRecord by ID and update its entry in the cached list for
 * the given Master Tag. Used after a write that changes server-computed
 * metrics on the CML (currently: ThicknessReading INSERT triggers parent
 * recompute via CmlMetricsCalculator). Cheaper than refetching the whole
 * Master Tag's CML list, and the UX gain — only the affected row blinks
 * fresh — is worth the extra helper.
 *
 * @param {string} masterTagId — parent (for cache update)
 * @param {string} cmlRecordId — the CML to refresh
 * @returns {Promise<object|null>} the refreshed CmlRecordDto, or null if not found
 */
export async function refreshCmlRecord(masterTagId, cmlRecordId) {
  if (!cmlRecordId) {
    debugWarn('[useMonitoringTab] refreshCmlRecord called with empty cmlRecordId — ignoring');
    return null;
  }

  try {
    const response = await httpService.get({
      url: `/api/v1/cmls/${cmlRecordId}`,
      headers: facilityHeaders(),
    });
    const refreshed = normalizeCmlRecord(response && response.data);
    if (!refreshed) return null;

    if (masterTagId) {
      const list = monitoringTabState.cmlsByMasterTag[masterTagId] || [];
      const next = list.map((c) => (c.id === cmlRecordId ? refreshed : c));
      Vue.set(monitoringTabState.cmlsByMasterTag, masterTagId, next);
    }
    debugLog(`[useMonitoringTab] refreshCmlRecord(${cmlRecordId}) → ok`);
    return refreshed;
  } catch (err) {
    debugError(`[useMonitoringTab] refreshCmlRecord(${cmlRecordId}) failed`, err);
    return null;
  }
}

// ─── Public API: ThicknessReading list ───────────────────────────────────────

/**
 * Fetch the ThicknessReading list for a TmlPoint (FR-007). Stores the result
 * in `monitoringTabState.readingsByTmlPoint[tmlPointId]`. Phase 1's endpoint
 * returns the latest 50 by default — the Monitoring tab renders the first 25
 * (FR-008) with a "Show all N" affordance to expand the rest.
 *
 * @param {string} tmlPointId
 * @returns {Promise<void>}
 */
export async function loadReadingsForTmlPoint(tmlPointId) {
  if (!tmlPointId) {
    debugWarn('[useMonitoringTab] loadReadingsForTmlPoint called with empty tmlPointId — ignoring');
    return;
  }

  const key = `readings:${tmlPointId}`;
  const controller = refreshAbortController(key);

  Vue.set(monitoringTabState.loadingReadingsForTmlPoint, tmlPointId, true);
  Vue.set(monitoringTabState.errorReadingsForTmlPoint, tmlPointId, null);

  try {
    // Request the max page size (500 per Phase 1
    // ThicknessReadingsController.GetForTmlPoint signature) so the FR-008
    // "Show all N readings…" affordance can honestly reveal the full
    // history for typical TmlPoints. The first 25 render initially per
    // FR-008; the remainder is in the same in-memory array, no further
    // fetch needed. TmlPoints with >500 readings are a Phase 3 concern
    // (server-side pagination needed) — flagged for follow-up rather
    // than blocking Wave C-δ.
    const response = await httpService.get({
      url: `/api/v1/tml-points/${tmlPointId}/thickness-readings`,
      params: { itemsPerPage: 500 },
      headers: facilityHeaders(),
      signal: controller.signal,
    });
    const items = (response && response.data && response.data.items) || [];
    Vue.set(monitoringTabState.readingsByTmlPoint, tmlPointId, items);
    debugLog(`[useMonitoringTab] loadReadingsForTmlPoint(${tmlPointId}) → ${items.length} rows`);
  } catch (err) {
    if (isAbortError(err)) {
      debugLog(`[useMonitoringTab] loadReadingsForTmlPoint(${tmlPointId}) aborted — discarding`);
      return;
    }
    debugError(`[useMonitoringTab] loadReadingsForTmlPoint(${tmlPointId}) failed`, err);
    Vue.set(
      monitoringTabState.errorReadingsForTmlPoint,
      tmlPointId,
      (err && err.message) || 'Failed to load ThicknessReadings.'
    );
  } finally {
    finalizeFetch(key, controller, monitoringTabState.loadingReadingsForTmlPoint, tmlPointId);
  }
}

// ─── Expand state ────────────────────────────────────────────────────────────

/**
 * Toggle the expanded state for a CmlRecord row. When expanding for the first
 * time, callers should also invoke `loadTmlPointsForCml(cmlRecordId)` — the
 * lazy-fetch trigger is wired by T040 in slice C.
 *
 * @param {string} cmlRecordId
 * @returns {boolean} The new expanded state.
 */
export function toggleCmlExpanded(cmlRecordId) {
  if (!cmlRecordId) return false;
  const current = !!monitoringTabState.expandedCmls[cmlRecordId];
  Vue.set(monitoringTabState.expandedCmls, cmlRecordId, !current);
  return !current;
}

/**
 * Toggle the expanded state for a TmlPoint row.
 *
 * @param {string} tmlPointId
 * @returns {boolean}
 */
export function toggleTmlPointExpanded(tmlPointId) {
  if (!tmlPointId) return false;
  const current = !!monitoringTabState.expandedTmlPoints[tmlPointId];
  Vue.set(monitoringTabState.expandedTmlPoints, tmlPointId, !current);
  return !current;
}

// ─── Per-key cancellation (R-9 — collapse-cancel-in-flight) ──────────────────

/**
 * Cancel a specific in-flight fetch by key. Used when a panel is collapsed
 * before its lazy-fetch resolves so we don't waste bandwidth + server cycles
 * on a result the UI won't render.
 *
 * @param {string} key  the fetch key, e.g. "tmlpoints:abc-123"
 * @returns {boolean}   true if an in-flight controller was aborted
 */
function cancelByKey(key) {
  const controller = abortControllers.get(key);
  if (!controller) return false;
  controller.abort();
  abortControllers.delete(key);
  return true;
}

/**
 * Cancel an in-flight loadTmlPointsForCml fetch (R-9). Safe to call when no
 * fetch is in flight — returns false and is a no-op.
 *
 * @param {string} cmlRecordId
 */
export function cancelLoadTmlPointsForCml(cmlRecordId) {
  if (!cmlRecordId) return;
  if (cancelByKey(`tmlpoints:${cmlRecordId}`)) {
    Vue.set(monitoringTabState.loadingTmlPointsForCml, cmlRecordId, false);
    debugLog(`[useMonitoringTab] cancelLoadTmlPointsForCml(${cmlRecordId})`);
  }
  // Drop the de-dup entry so a re-expand starts a FRESH fetch rather than
  // coalescing onto the request we just aborted (R-9 collapse → re-expand).
  // The identity guard in loadTmlPointsForCml's `.finally` ensures the aborted
  // promise settling later won't wipe whatever fresh entry replaced it.
  inFlightTmlPoints.delete(cmlRecordId);
}

/**
 * Cancel an in-flight loadReadingsForTmlPoint fetch (R-9).
 *
 * @param {string} tmlPointId
 */
export function cancelLoadReadingsForTmlPoint(tmlPointId) {
  if (!tmlPointId) return;
  if (cancelByKey(`readings:${tmlPointId}`)) {
    Vue.set(monitoringTabState.loadingReadingsForTmlPoint, tmlPointId, false);
    debugLog(`[useMonitoringTab] cancelLoadReadingsForTmlPoint(${tmlPointId})`);
  }
}

// ─── Cleanup ─────────────────────────────────────────────────────────────────

/**
 * Abort every in-flight fetch managed by this composable. Call from
 * MonitoringTab.vue's `beforeDestroy` hook to prevent stale UI writes after
 * the tab is unmounted (e.g., user navigates away while a fetch is pending).
 */
export function abortAllInFlight() {
  if (abortControllers.size > 0) {
    debugLog(`[useMonitoringTab] abortAllInFlight aborting ${abortControllers.size} requests`);
    abortControllers.forEach((controller) => controller.abort());
    abortControllers.clear();
  }
  // Always clear the de-dup map so post-unmount re-mounts start fresh, even if
  // a settle hasn't run yet (lifecycle pairs with abortControllers).
  inFlightTmlPoints.clear();
  _inspectionCmlsInFlight.clear();
  _inspectionMasterTagIdsInFlight.clear();
}

// ─── Display helpers (kept local — match usePopupCard.js conventions) ───────

/**
 * Format a decimal number with a fixed digit count. Returns the FR-018 dash
 * placeholder for null / NaN.
 *
 * @param {number|null|undefined} value
 * @param {number} digits
 * @returns {string}
 */
export function formatDecimal(value, digits) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(digits);
}

/**
 * Format an ISO date string for human display. Returns the FR-018 dash
 * placeholder for null / invalid input.
 *
 * @param {string|null|undefined} isoStr
 * @returns {string}
 */
export function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch (err) {
    debugWarn('[useMonitoringTab] formatDate failed for value', isoStr, err);
    return '—';
  }
}

// T043 — Status color/label helpers are now the canonical source at
// `@/utils/cmlStatus`. Re-exported here so slice-B callers (CmlRecordRow.vue)
// continue to work without changing their import path. The single-source-of-
// truth discipline satisfies SC-002 (marker + popup + Monitoring all consume
// identical color/label maps).
export { statusColorForCml, statusLabelForCml } from '@/utils/cmlStatus';
